import React, { useEffect, useState } from 'react';
import { MessageCircleQuestion, Search, Loader2, Plus, ArrowUp, MessageSquare, ArrowLeft, Send } from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import {
  popListQuestions, popAskQuestion, popGetQuestion, popAnswerQuestion, popUpvoteQuestion, roleLabel,
} from '../../lib/popApi';

const AskForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: '', body: '', tags_csv: '' });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const payload = {
        title: f.title, body: f.body,
        tags: f.tags_csv.split(',').map((s) => s.trim()).filter(Boolean),
      };
      await popAskQuestion(payload);
      setOpen(false); setF({ title: '', body: '', tags_csv: '' });
      onCreated();
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not post'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-6">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="question-ask-open"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold">
          <Plus className="w-4 h-4" /> Ask a practitioner
        </button>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 space-y-3">
          <input placeholder="Title (e.g. How do you evaluate OT-IT integration risk in a legacy plant?)"
            value={f.title} onChange={upd('title')} data-testid="question-form-title"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <textarea placeholder="Provide the context — what have you tried? What outcome are you seeking?"
            value={f.body} onChange={upd('body')} rows={5} data-testid="question-form-body"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Tags (comma-separated) — e.g. AI, Manufacturing, Change-Mgmt"
            value={f.tags_csv} onChange={upd('tags_csv')} data-testid="question-form-tags"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          {err && <div className="text-xs text-rose-300">{err}</div>}
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy || !f.title.trim() || !f.body.trim()}
              data-testid="question-form-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-sm font-semibold">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Post question
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm text-white/70">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionRow = ({ q, onOpen, onUpvote, myUserId }) => {
  const upvoted = q.upvotes?.includes(myUserId);
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5 hover:border-white/20 transition cursor-pointer"
      onClick={() => onOpen(q.id)} data-testid={`question-row-${q.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={(e) => { e.stopPropagation(); onUpvote(q.id); }}
          data-testid={`question-upvote-${q.id}`}
          className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-md border transition ${
            upvoted ? 'bg-amber-300/15 border-amber-300/40 text-amber-200' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
          }`}>
          <ArrowUp className="w-3.5 h-3.5" />
          <span className="text-[10px] font-bold tabular-nums">{q.upvotes?.length || 0}</span>
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-base font-serif font-semibold text-white leading-tight mb-1">{q.title}</div>
          <div className="text-xs text-white/70 line-clamp-2 mb-2">{q.body}</div>
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-white/50">
            <span>Asked by <span className="text-white/80 font-medium">{q.asker_name}</span> · {roleLabel(q.asker_role)}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {q.answer_count} answer{q.answer_count === 1 ? '' : 's'}</span>
            {q.tags?.length > 0 && q.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionDetail = ({ id, onBack, onAnswered }) => {
  const { user } = usePoPAuth();
  const [q, setQ] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(true);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setBusy(true);
    try {
      const r = await popGetQuestion(id);
      setQ(r.question); setAnswers(r.answers || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async () => {
    if (!body.trim()) return;
    setPosting(true); setErr('');
    try {
      const r = await popAnswerQuestion(id, body.trim());
      setAnswers((prev) => [...prev, r.answer]);
      setBody(''); onAnswered && onAnswered();
    } catch (e) { setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not post'); }
    finally { setPosting(false); }
  };

  if (busy) return <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  if (!q) return null;

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mb-4" data-testid="question-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to all questions
      </button>
      <div className="rounded-xl border border-white/10 bg-slate-900/40 p-6 mb-6">
        <h2 className="font-serif text-2xl text-white mb-2">{q.title}</h2>
        <div className="text-[11px] text-white/60 mb-4">
          Asked by <span className="text-white/85 font-medium">{q.asker_name}</span> · {roleLabel(q.asker_role)}
        </div>
        <div className="text-sm text-white/85 whitespace-pre-line leading-relaxed">{q.body}</div>
        {q.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {q.tags.map((t) => (
              <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{t}</span>
            ))}
          </div>
        )}
      </div>

      <div className="mb-3 text-[10px] tracking-[0.3em] font-bold text-amber-300">{answers.length} ANSWER{answers.length === 1 ? '' : 'S'}</div>
      <div className="space-y-3 mb-6">
        {answers.map((a) => (
          <div key={a.id} className="rounded-xl border border-white/10 bg-slate-900/40 p-4" data-testid={`answer-${a.id}`}>
            <div className="text-[11px] text-white/60 mb-2">
              <span className="text-white/85 font-medium">{a.answerer_name}</span> · <span className="text-amber-300">{roleLabel(a.answerer_role)}</span> · {new Date(a.created_at).toLocaleDateString()}
            </div>
            <div className="text-sm text-white/85 whitespace-pre-line leading-relaxed">{a.body}</div>
          </div>
        ))}
        {answers.length === 0 && <div className="text-xs text-white/50 italic">No answers yet — be the first.</div>}
      </div>

      {user && user.role !== 'unassigned' && (
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/60 mb-2">YOUR ANSWER</div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid="answer-body"
            placeholder="Share your practitioner insight — what worked, what didn&apos;t, and why."
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          {err && <div className="text-xs text-rose-300 mt-1">{err}</div>}
          <button onClick={submit} disabled={posting || !body.trim()} data-testid="answer-submit"
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-sm font-semibold">
            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Post answer
          </button>
        </div>
      )}
    </div>
  );
};

const PoPQuestions = () => {
  const { user } = usePoPAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = q ? { q } : {};
      const r = await popListQuestions(params);
      setItems(r.results || []);
    } catch (_e) { /* noop */ } finally { setBusy(false); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const doUpvote = async (id) => {
    await popUpvoteQuestion(id);
    load();
  };

  return (
    <PoPShell activeTab="questions">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">ASK A PRACTITIONER</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Real-world questions, real-world answers.</h1>
        <p className="text-sm text-white/60 mt-1 max-w-2xl">Students and faculty post situational questions — industry Professors of Practice answer with hard-won practitioner insight.</p>
      </div>

      {openId ? (
        <QuestionDetail id={openId} onBack={() => { setOpenId(null); load(); }} onAnswered={load} />
      ) : (
        <>
          {user && user.role !== 'unassigned' && <AskForm onCreated={load} />}
          <div className="relative mb-4">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="question-search"
              placeholder="Search questions…"
              className="w-full pl-9 pr-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
          </div>

          {busy ? (
            <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
              <MessageCircleQuestion className="w-8 h-8 text-white/30 mx-auto mb-2" />
              <div className="text-sm text-white/60">No questions yet. Be the first to ask.</div>
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map((qq) => <QuestionRow key={qq.id} q={qq} onOpen={setOpenId} onUpvote={doUpvote} myUserId={user?.user_id} />)}
            </div>
          )}
        </>
      )}
    </PoPShell>
  );
};

export default PoPQuestions;
